import {IAppState} from '../models/IAppState';
import {LocalstorageService} from '../services/localstorage.service';
import * as LocationActions from './actions/location.actions';
import * as UserActions from './actions/user.actions';
import * as StoreActions from './actions/store.actions';
import {Action, createReducer, on} from '@ngrx/store';
import {remove} from 'lodash';
import * as DeviceActions from './actions/device.actions';

const LocalStorageKey = 'AppState';

const initialState: IAppState = {
  language: 'en',
  isAuthorised: null,
  locationsState: {
    pending: false,
    error: null,
    locations: []
  },
  checkInsState: {
    pending: false,
    error: null,
    checkIns: null
  },
  authData: null,
  selectedLocation: null,
  selectedLocationError: null,
  userGeolocation: null,
  userData: null,
  loggedInAsLocationUser: false,
  locationUser: null,
  deviceInfo: null
};

const reducer = createReducer(
  initialState,
  on(StoreActions.saveToLocalStorage, (state: IAppState) => {
    return LocalstorageService.setSavedState(state, LocalStorageKey);
  }),
  on(StoreActions.loadFromLocalStorage, (state: IAppState) => {
    return LocalstorageService.getSavedState(state, LocalStorageKey);
  }),
  on(UserActions.setLanguage, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      language: action.language
    });
  }),
  on(UserActions.authorize, (state: IAppState, action: Action | any) => {
    if (action.isAuthorised) {
      return Object.assign({}, state, {
        isAuthorised: action.isAuthorised,
        authData: action.authData
      });
    } else {
      return Object.assign({}, initialState);
    }
  }),
  on(UserActions.setLocalUser, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      localUser: action.localUser
    });
  }),
  on(UserActions.setUserGeolocation, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      userGeolocation: action.userGeolocation
    });
  }),

  on(UserActions.getUserData, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      userData: action.userData
    });
  }),

  on(LocationActions.addLocationPersonalInfo, (state: IAppState, action: Action | any) => {
    let locations = state.locationsPersonalInfo || [];
    locations = locations.slice();
    remove(locations, l => action.locationPersonalInfo.locationId === l.locationId);
    locations.push(action.locationPersonalInfo);
    return Object.assign({}, state, {
      locationsPersonalInfo: locations
    });
  }),
  on(LocationActions.removeLocationPersonalInfo, (state: IAppState, action: Action | any) => {
    let locations = state.locationsPersonalInfo || [];
    locations = locations.slice();
    remove(locations, l => action.locationId === l.locationId);
    return Object.assign({}, state, {
      locationsPersonalInfo: locations
    });
  }),
  on(LocationActions.getLocations, (state: IAppState) => {
    return Object.assign({}, state, {
      locationsState: {
        locations: [],
        pending: true,
        error: null
      }
    });
  }),
  on(LocationActions.getLocationsSuccess, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      locationsState: {
        locations: action.locations,
        pending: false,
        error: null
      }
    });
  }),
  on(LocationActions.getLocationsError, (state: IAppState) => {
    return Object.assign({}, state, {
      locationsState: {
        locations: [],
        pending: false,
        error: 'Error'
      }
    });
  }),

  on(LocationActions.getNearby, (state: IAppState) => {
    return Object.assign({}, state, {
      locationsState: {
        locations: state.locationsState.locations,
        pending: true,
        error: null
      }
    });
  }),
  on(LocationActions.getNearbySuccess, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      locationsState: {
        locations: action.locations.locations,
        pending: false,
        error: null
      }
    });
  }),
  on(LocationActions.getNearbyError, (state: IAppState) => {
    return Object.assign({}, state, {
      locationsState: {
        locations: [],
        pending: false,
        error: 'Error'
      }
    });
  }),

  on(LocationActions.getCheckIns, (state: IAppState) => {
    return Object.assign({}, state, {
      checkInsState: {
        checkIns: state.checkInsState.checkIns,
        pending: true,
        error: null
      }
    });
  }),
  on(LocationActions.getCheckInsSuccess, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      checkInsState: {
        checkIns: action.checkIns,
        pending: false,
        error: null
      }
    });
  }),
  on(LocationActions.getCheckInsError, (state: IAppState) => {
    return Object.assign({}, state, {
      checkInsState: {
        checkIns: null,
        pending: false,
        error: 'Error'
      }
    });
  }),

  on(LocationActions.getLocation, (state: IAppState) => {
    return Object.assign({}, state, {
      selectedLocation: null,
      selectedLocationError: null
    });
  }),
  on(LocationActions.getLocationSuccess, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      selectedLocation: action.selectedLocation,
      selectedLocationError: null
    });
  }),
  on(LocationActions.getLocationError, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      selectedLocation: null,
      selectedLocationError: action
    });
  }),

  on(LocationActions.setCheckIn, (state: IAppState, action: Action | any) => {
    if (state?.selectedCheckIn?.id === action.checkin?.id) {
      return Object.assign({}, state, {
        selectedCheckIn: action.checkin
      });
    }
    return state;
  }),

  on(LocationActions.getCheckIn, (state: IAppState) => {
    return Object.assign({}, state, {
      selectedCheckIn: null
    });
  }),
  on(LocationActions.getCheckInSuccess, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      selectedCheckIn: action.selectedCheckIn
    });
  }),
  on(LocationActions.getCheckInError, (state: IAppState) => {
    return Object.assign({}, state, {
      selectedCheckIn: null
    });
  }),

  on(LocationActions.checkIn, (state: IAppState) => {
    return Object.assign({}, state, {
      selectedLocation: null
    });
  }),

  on(UserActions.loginAsLocationUser, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      loggedInAsLocationUser: action.loggedInAsLocationUser,
      locationUser: action.locationUser
    });
  }),

  on(DeviceActions.setDeviceTokenAndOperatingSystem, (state: IAppState, action: Action | any) => {
    return Object.assign({}, state, {
      deviceInfo: action.deviceInfo
    });
  })
);

export function AppStateReducer(state = initialState, action: Action | any ) {
  return reducer(state, action);
}


